import { useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export function AdminLayout() {
  const { user, role, loading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const menuItems = [
    { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/admin', roles: ['admin', 'editor', 'viewer'] },
    { icon: 'ri-file-list-line', label: 'Sayfalar', path: '/admin/pages', roles: ['admin', 'editor'] },
    { icon: 'ri-menu-line', label: 'Menü', path: '/admin/menus', roles: ['admin'] },
    { icon: 'ri-home-line', label: 'Ana Sayfa', path: '/admin/home', roles: ['admin', 'editor'] },
    { icon: 'ri-service-line', label: 'Hizmetler', path: '/admin/services', roles: ['admin', 'editor'] },
    { icon: 'ri-article-line', label: 'Blog', path: '/admin/blog', roles: ['admin', 'editor'] },
    { icon: 'ri-image-line', label: 'Medya', path: '/admin/media', roles: ['admin', 'editor'] },
    { icon: 'ri-mail-line', label: 'Formlar', path: '/admin/forms', roles: ['admin'] },
    { icon: 'ri-search-line', label: 'SEO', path: '/admin/seo', roles: ['admin', 'editor'] },
    { icon: 'ri-user-line', label: 'Kullanıcılar', path: '/admin/users', roles: ['admin'] },
    { icon: 'ri-settings-line', label: 'Ayarlar', path: '/admin/settings', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(role as string)
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">3DP Makina</h1>
          <p className="text-sm text-gray-600 mt-1">Admin Panel</p>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-1">
            {filteredMenuItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/admin'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-[#C1121F] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  <i className={`${item.icon} text-lg`}></i>
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <i className="ri-user-line text-gray-600"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
              <p className="text-xs text-gray-600 capitalize">{role}</p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-logout-box-line"></i>
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
